/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, MessageCircle, Users, BookOpen, Settings, LogOut, LayoutDashboard, BarChart2, Upload, Terminal, Warehouse, PenTool, Landmark, UserPlus, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useConnectAccess from '../hooks/useConnectAccess';

/**
 * Sidebar Navigation
 * Phase 0.8 P6: Restructured from 9 items to 5+1
 * Conference Sprint S1a: Renamed x-Connect to Community
 * Conference Sprint S2f: Added Cricket Dashboard for Civil Construction organizations
 *
 * Main items: Home, Workspace, SkillBot, Community, Knowledge Base
 * Conditional: Cricket Dashboard (for organization + Civil/Civil Construction)
 * Bottom: Settings, Logout
 *
 * Restored:
 * - Upload Profile → /upload (reconnected in safety stabilization)
 *
 * Removed from sidebar (still accessible via routes):
 * - My Roadmap → tab in Workspace
 * - Learning Hub → tab in Workspace
 * - Network Intelligence → now "My Network" tab in Community
 */
const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  // R14 bugfix (2026-10-08): Connect entry is shown to end users whose
  // User.connectAccess=true, carried on /users/me as features.connect.
  // useConnectAccess reads the cached value synchronously, so the link
  // doesn't flicker on refresh for an owner who already has access.
  const { hasAccess: hasConnectAccess } = useConnectAccess();

  const isActive = (path) => {
    // Handle both / and /dashboard for Home
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    // Handle Community - includes any /community routes
    if (path === '/community') {
      return location.pathname === '/community' || location.pathname.startsWith('/community');
    }
    // Handle Connect - lights up on /connect and any nested /connect/*.
    if (path === '/connect') {
      return location.pathname === '/connect' || location.pathname.startsWith('/connect/');
    }
    // Handle Upload Profile - matches /upload, /upload-resume, /upload-profile
    if (path === '/upload') {
      return location.pathname === '/upload' || location.pathname === '/upload-resume' || location.pathname === '/upload-profile';
    }
    // Handle Cricket Dashboard
    if (path === '/cricket-dashboard') {
      return location.pathname === '/cricket-dashboard';
    }
    // Handle Putaway Intelligence (and any future /wms/* siblings) — same nested
    // prefix pattern used by Community so /wms/putaway-intelligence/foo also lights up.
    if (path === '/wms/putaway-intelligence') {
      return location.pathname === '/wms/putaway-intelligence' || location.pathname.startsWith('/wms/putaway-intelligence');
    }
    // Handle RCR-Layout — same nested-prefix pattern for future sub-routes.
    if (path === '/rcr-layout') {
      return location.pathname === '/rcr-layout' || location.pathname.startsWith('/rcr-layout');
    }
    return location.pathname === path;
  };

  // Check if user should see Cricket Dashboard (organization + Civil Construction)
  // Conference Sprint S2f: Cricket Dashboard for Civil Construction organizations
  const isCivilConstruction = user?.vertical === 'Civil' || user?.vertical === 'Civil Construction';
  const isOrganization = user?.accountType === 'organization' || user?.entityType === 'organization';
  const showCricketDashboard = isOrganization && isCivilConstruction;

  // Putaway Intelligence: WMS-domain research artifact, visible to all 4 entity
  // types (individual / organization / university / institute) provided the
  // user's vertical is WMS.
  const isWMS = user?.vertical === 'WMS';

  // RCR-Layout: Civil-vertical layout planner. Visible to any entity type
  // (individual / organization / university / institute) provided the user's
  // vertical is Civil — broader than Cricket Dashboard, which is org-only.
  const showRcrLayout = isCivilConstruction;

  // ShubbhYatra Phase 1: badge visibility. Mirrors the server-side gates
  // (features.connectShubbhYatra, features.shubbhYatraOnly) and the
  // @shubbhyatra.com email fallback so the badge appears on first paint
  // even before /me features are folded in. Non-ShubbhYatra users:
  // isShubbhYatra === false, badge never renders.
  const email = String(user?.email || '').toLowerCase();
  const isShubbhYatra =
    user?.features?.connectShubbhYatra === true
    || user?.features?.shubbhYatraOnly === true
    || /@shubbhyatra\.com$/.test(email);

  // Main navigation items (5 core + conditional Cricket Dashboard)
  // Travel domain (Phase 1): for travel-ONLY users (shubbhYatraOnly=true),
  // filter nav items down to the modules they're actually enabled for.
  // Non-travel-only users: `filteredNavItems === navItems` byte-identical,
  // zero behaviour change. Travel users with allowSkillDelivery=true also
  // get the full list — only travel-only is restricted.
  const isTravelOnly = user?.features?.shubbhYatraOnly === true;
  const travelModules = Array.isArray(user?.shubbhYatraConfig?.modules)
    ? user.shubbhYatraConfig.modules
    : null;

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/workspace', icon: LayoutDashboard, label: 'Workspace' },
    { path: '/skillbot', icon: MessageCircle, label: 'SkillBot' },
    { path: '/skillcode', icon: Terminal, label: 'SkillCode' },
    { path: '/community', icon: Users, label: 'Community' },
    // Connect entry: owner-area invite + contact workspace. Shown only
    // to users with User.connectAccess=true (features.connect from
    // /users/me, cached by useConnectAccess).
    ...(hasConnectAccess ? [{ path: '/connect', icon: UserPlus, label: 'Connect' }] : []),
    { path: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
    { path: '/upload', icon: Upload, label: 'Upload Profile' },
    // Travel Learning: appears only when 'learning' is in a travel
    // user's resolved modules. Non-travel users NEVER see this entry.
    ...((isTravelOnly && travelModules && travelModules.includes('learning'))
      ? [{ path: '/learning', icon: GraduationCap, label: 'Learning' }]
      : []),
    // Cricket Dashboard - only for Civil Construction organization users
    ...(showCricketDashboard ? [{ path: '/cricket-dashboard', icon: BarChart2, label: 'Cricket Dashboard' }] : []),
    // Skill.Civil Showcase — Civil-vertical family/firm case studies (public route,
    // linked from the authed sidebar so Civil users can jump to their showcase).
    // Points at the featured Ratwani case study; the list is one back-click away.
    ...(isCivilConstruction ? [{ path: '/domain/civil/case-studies/ratwani', icon: Landmark, label: 'Skill.Civil Showcase' }] : []),
    // Putaway Intelligence - WMS-domain research case study
    ...(isWMS ? [{ path: '/wms/putaway-intelligence', icon: Warehouse, label: 'Putaway Intelligence' }] : []),
    // RCR-Layout - Civil-vertical layout planner (PR1 scaffold)
    ...(showRcrLayout ? [{ path: '/rcr-layout', icon: PenTool, label: 'RCR-Layout (Beta)' }] : []),
  ];

  // Travel-only module map: each item has its OWN module token. An
  // item is hidden for a travel-only user unless its mapped module
  // appears in `travelModules`. Non-travel-only users bypass this map
  // entirely (filteredNavItems === navItems below).
  const PATH_TO_MODULE = {
    '/skillbot':       'skillbot',
    '/skillcode':      'skillcode',
    '/connect':        'connect',
    '/community':      'community',
    '/knowledge-base': 'knowledge',
    '/upload':         'profile',
    '/workspace':      'roadmap',
    '/learning':       'learning',
    '/cricket-dashboard': 'cricket', // vertical-specific, never on for travel
    '/wms/putaway-intelligence': 'wms',
    '/rcr-layout': 'civil',
    '/domain/civil/case-studies/ratwani': 'civil'
  };
  const filteredNavItems = (isTravelOnly && travelModules)
    ? navItems.filter((it) => {
        // Home is always visible; everything else gated by its mapped module.
        if (it.path === '/dashboard') return true;
        const mod = PATH_TO_MODULE[it.path];
        if (!mod) return false; // conservative: unknown path hidden for travel-only
        return travelModules.includes(mod);
      })
    : navItems;

  // Bottom navigation items
  const bottomItems = [
    { path: '/profile', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="w-64 bg-white shadow-lg flex flex-col border-r border-gray-200">
      {/* Header with Logo */}
      <div className="p-6 border-b border-gray-200">
        <img
          src="/logo.jpg"
          alt="SkillDelivery Logo"
          className="w-40 h-auto mx-auto mb-2"
        />
        <p className="text-xs text-center text-gray-600">Skills, Delivered!</p>
        {/* ShubbhYatra co-brand badge — only for ShubbhYatra users. */}
        {isShubbhYatra && (
          <div
            className="mt-3 mx-auto flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm"
            style={{ width: 'fit-content' }}
            aria-label="with ShubbhYatra"
          >
            <span className="text-[10px] uppercase tracking-wide text-gray-500">with</span>
            <img
              src="/shubbhyatra-logo.png"
              alt="ShubbhYatra"
              style={{ height: 28, width: 'auto', display: 'block' }}
            />
          </div>
        )}
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold">
            {(user?.displayName || user?.name || user?.fullName || 'U')[0]}
          </div>
          <div>
            <p className="font-semibold text-gray-800">{user?.displayName || user?.name || user?.fullName || 'User'}</p>
            <p className="text-xs text-cyan-600">{user?.vertical}</p>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {filteredNavItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 font-semibold shadow-md'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section: Settings + Logout */}
      <div className="p-4 border-t border-gray-200">
        <ul className="space-y-2">
          {bottomItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 font-semibold shadow-md'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
          <li>
            <button
              onClick={logout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;